import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MenuItem } from 'primeng/api';
import {
  announcementStatusLabelKey,
  announcementStatusSeverity,
  isCancellable,
  isCorrectable,
  isEditable,
  isRetractable,
  summarizeTargeting,
  TagSeverity
} from '../announcement-list/announcement-labels';
import { Announcement, AnnouncementStatus } from '../announcement-list/graphql';

/**
 * Eine Zeile der Announcement-Liste (#738).
 *
 * Reine Praesentation: haelt keinen Zustand, stellt keine Query, ruft keine Mutation. Alle fuenf
 * Aktionen — abbrechen, aendern, zurueckziehen, korrigieren, duplizieren — gehen als Ereignis nach
 * oben; die Liste kennt Dialog, Toast, Navigation und Neuladen.
 *
 * Uebersetzt wird durchgehend im Template ueber die translate-Pipe (auch die parametrisierten
 * Schluessel), nicht ueber TranslateService.instant in einem computed: instant liest den Stand zum
 * Zeitpunkt der Auswertung und ein Sprachwechsel wuerde die Zeile nicht erreichen.
 *
 * Muster nach notification-delivery-row (#737): Doppelform-Selector, OnPush, Signal-input()/output(),
 * host-Klassen. Bewusst NICHT standalone — siehe .github/copilot-instructions.md.
 */
@Component({
  selector: 'div[announcement-row], app-announcement-row',
  templateUrl: './announcement-row.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'class': 'block bg-white border-b border-gray'
  }
})
export class AnnouncementRowComponent {

  readonly announcement = input.required<Announcement>();

  /**
   * Server-uebersetztes Label des Typs aus dem Katalog. Fehlt es (Katalog nicht geladen, Typ nicht
   * mehr im Katalog), bleibt der technische typeKey stehen — brauchbarer als eine leere Zelle.
   */
  readonly typeLabel = input<string | null>(null);

  // `cancel` waere ein nativer DOM-Event-Name (@angular-eslint/no-output-native).
  readonly cancelRequested = output<Announcement>();
  readonly editRequested = output<Announcement>();
  readonly retractRequested = output<Announcement>();
  readonly correctRequested = output<Announcement>();
  readonly duplicateRequested = output<Announcement>();

  readonly statusLabelKey = computed(() => announcementStatusLabelKey(this.announcement().status));
  readonly statusSeverity = computed<TagSeverity>(() => announcementStatusSeverity(this.announcement().status));

  /**
   * Titel aus content.de.title — `de` ist die einzige Sprache, die der Server erzwingt, und damit die
   * einzige, die garantiert vorhanden ist. content ist ein pass-through-Map-Scalar, der Zugriff
   * deshalb defensiv.
   */
  readonly title = computed(() => this.announcement().content?.['de']?.title || '');

  readonly typeDisplay = computed(() => this.typeLabel() || this.announcement().typeKey);

  /**
   * Zielgruppe in Kurzform. targeting ist ein pass-through-Map-Scalar und kann auch Formen enthalten,
   * die dieses Frontend nie erzeugt hat — summarizeTargeting greift deshalb defensiv zu.
   */
  readonly targeting = computed(() => summarizeTargeting(this.announcement().targeting));

  /**
   * Fortschritt zaehlt MANDANTEN, nicht Empfaenger — die Beschriftung muss das klar machen, sonst
   * liest jemand Reichweite hinein. Wie viele Personen erreicht wurden, sagt die API nicht.
   *
   * Angezeigt ab RUNNING: bei SCHEDULED stuende dort nur „0 von n" und suggerierte einen begonnenen
   * Versand, den es noch nicht gibt. RETRACTED gehoert dazu — die Zahl beantwortet, wie weit das
   * Original gekommen war, und bleibt nach einem Rueckzug richtig. Wie weit der RUECKZUG gekommen
   * ist, ist eine andere Frage: die steht in retractionProgress und nur auf der Mutationsantwort.
   */
  readonly showProgress = computed(() => {
    const status = this.announcement().status;
    return status === AnnouncementStatus.RUNNING
      || status === AnnouncementStatus.DONE
      || status === AnnouncementStatus.RETRACTED;
  });

  readonly progressParams = computed(() => {
    const progress = this.announcement().fanoutProgress;
    return {
      completed: progress?.completedTenants ?? 0,
      total: progress?.totalTenants ?? 0
    };
  });

  readonly cancellable = computed(() => isCancellable(this.announcement().status));

  /**
   * Eine Zeile, die eine andere Ankuendigung richtigstellt (Client-Dok §8.2). Ohne Kennzeichnung
   * stehen zwei fast gleiche Titel untereinander und niemand weiss, welcher der gueltige ist.
   *
   * Ein Verweis zurueck auf das Original wird bewusst NICHT angeboten: der Client kennt nur die Id,
   * und eine Einzelabfrage gibt es im Schema nicht.
   */
  readonly isCorrection = computed(() => !!this.announcement().correctsAnnouncementId);

  /**
   * Zeilen-Aktionen, in der Reihenfolge des Lebenszyklus: abbrechen (solange nichts raus ist),
   * aendern, zurueckziehen, korrigieren, duplizieren. Nach Folgenschwere zu sortieren waere die
   * falsche Ordnung — der Nutzer sucht die Aktion zum Zustand, nicht die harmloseste.
   *
   * Welche erscheint, entscheiden ausschliesslich die Praedikate aus announcement-labels: sie
   * spiegeln die Allow-List-switches des Servers. Zwei Faelle sind dabei nicht offensichtlich:
   *
   * - Zurueckziehen erscheint AUCH bei RETRACTED — ein zweiter Aufruf ist die Wiederaufnahme eines
   *   unvollstaendigen Durchlaufs, nicht ein Fehler.
   * - Korrigieren erscheint dort NICHT — eine zurueckgezogene Ankuendigung laesst sich nie mehr
   *   richtigstellen. Diese Einbahnstrasse spricht der Rueckzugs-Dialog vorher an.
   *
   * Die Labels sind i18n-Schluessel: nx-default-button schickt auch menuItems-Labels durch die
   * translate-Pipe (default-button.html:33).
   *
   * Bewusst als Menue, auch wenn nur ein Eintrag uebrig bleibt: nx-default-button feuert bei
   * nicht-leerem [menuItems] weder (clicked) noch (click) (default-button.component.ts:150), ein
   * zweiter Darstellungspfad waere also auch ein zweites Verhalten.
   */
  readonly menuItems = computed<MenuItem[]>(() => {
    const status = this.announcement().status;
    const items: MenuItem[] = [];

    if (isCancellable(status)) {
      items.push({
        label: 'core.announcement.row.cancelAction',
        command: () => this.cancelRequested.emit(this.announcement())
      });
    }
    if (isEditable(status)) {
      items.push({
        label: 'core.announcement.row.editAction',
        command: () => this.editRequested.emit(this.announcement())
      });
    }
    if (isRetractable(status)) {
      items.push({
        label: 'core.announcement.row.retractAction',
        command: () => this.retractRequested.emit(this.announcement())
      });
    }
    if (isCorrectable(status)) {
      items.push({
        label: 'core.announcement.row.correctAction',
        command: () => this.correctRequested.emit(this.announcement())
      });
    }
    // Immer: aus einer beliebigen Zeile eine NEUE Ankuendigung machen ist von jedem Status aus
    // sinnvoll — auch von einer abgebrochenen, die genau deshalb abgebrochen wurde.
    items.push({
      label: 'core.announcement.row.duplicateAction',
      command: () => this.duplicateRequested.emit(this.announcement())
    });
    return items;
  });
}
